import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleSessionExpired, refreshToken } from "../utils/methods";
import { useAlert } from "../utils/AlertContext";
import { TranslationsContext } from "../utils/TranslationsContext";

export const SESSION_WARNING_BEFORE_MS = 2 * 60 * 1000;
// setTimeout overflows (fires immediately) past 2^31-1 ms.
const MAX_TIMEOUT_MS = 2147483647;

const getTokenExpiryMs = () => {
  const token = localStorage.getItem("authToken");
  if (!token) return null;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// Drives the "session expiring" warning off the JWT's own `exp` rather than a
// countdown, so it stays correct after the tab was backgrounded (timers get
// throttled or frozen there) — every return to the tab re-reads the token and
// re-derives where we are relative to expiry.
const useSessionExpiry = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useContext(TranslationsContext);

  const [expiresAt, setExpiresAt] = useState(getTokenExpiryMs);
  const [now, setNow] = useState(() => Date.now());
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    const sync = () => {
      setExpiresAt(getTokenExpiryMs());
      setNow(Date.now());
    };
    const onStorage = (e) => {
      // Another tab refreshed or cleared the token.
      if (e.key === "authToken" || e.key === null) sync();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };

    window.addEventListener("auth-token-changed", sync);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("auth-token-changed", sync);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const remainingMs = expiresAt ? expiresAt - now : null;
  const inWarning = remainingMs !== null && remainingMs <= SESSION_WARNING_BEFORE_MS;

  // Outside the warning window: one timeout to wake up when it starts.
  // Inside it: tick every second for the countdown.
  useEffect(() => {
    if (!expiresAt) return;
    if (!inWarning) {
      const delay = Math.min(expiresAt - SESSION_WARNING_BEFORE_MS - Date.now(), MAX_TIMEOUT_MS);
      const id = setTimeout(() => setNow(Date.now()), Math.max(delay, 0));
      return () => clearTimeout(id);
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt, inWarning]);

  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0) {
      handleSessionExpired(showAlert, navigate, t);
    }
  }, [remainingMs, showAlert, navigate, t]);

  const extend = useCallback(async () => {
    setExtending(true);
    const newToken = await refreshToken();
    setExtending(false);
    if (newToken) return; // auth-token-changed re-syncs and closes the warning

    const exp = getTokenExpiryMs();
    if (!exp || exp <= Date.now()) {
      handleSessionExpired(showAlert, navigate, t);
    } else {
      // Likely a network blip — keep the warning up so they can retry.
      showAlert(t("SESSION_EXTEND_FAILED"), "error");
    }
  }, [showAlert, navigate, t]);

  return {
    showWarning: inWarning && remainingMs > 0,
    remainingMs: Math.max(remainingMs ?? 0, 0),
    extend,
    extending,
  };
};

export default useSessionExpiry;
